<script setup lang="ts">
import type { MapProvider } from '@gpx-spa/geo-domain';
import {
  computed,
  onBeforeUnmount,
  onMounted,
  provide,
  ref,
  toRef,
} from 'vue';
import { useMapProviderContainerResize } from '../composables/useMapProviderContainerResize';
import { GPX_MAP_PROVIDER_KEY } from '../map/gpx-map-injection-key';

const props = withDefaults(
  defineProps<{
    mapProvider: MapProvider;
    observeResize?: boolean;
    resizeDebounceMs?: number;
  }>(),
  {
    observeResize: true,
    resizeDebounceMs: 50,
  },
);

defineSlots<{
  'top-left'?: () => unknown;
  'top-center'?: () => unknown;
  'top-right'?: () => unknown;
  'left-center'?: () => unknown;
  'right-center'?: () => unknown;
  'bottom-left'?: () => unknown;
  'bottom-center'?: () => unknown;
  'bottom-right'?: () => unknown;
}>();

const emit = defineEmits<{
  ready: [];
  error: [message: string];
}>();

const root = ref<HTMLElement | null>(null);
const mountError = ref<string | null>(null);
const mapMountedOk = ref(false);

const resizeActive = computed(
  () => mapMountedOk.value && mountError.value === null,
);

useMapProviderContainerResize({
  containerRef: root,
  mapProvider: props.mapProvider,
  active: resizeActive,
  observeResize: toRef(props, 'observeResize'),
  resizeDebounceMs: toRef(props, 'resizeDebounceMs'),
});

provide(GPX_MAP_PROVIDER_KEY, toRef(props, 'mapProvider'));

onMounted(async () => {
  if (!root.value) return;
  mountError.value = null;
  try {
    await Promise.resolve(props.mapProvider.mount(root.value));
    mapMountedOk.value = true;
    emit('ready');
  } catch (e) {
    props.mapProvider.destroy();
    mapMountedOk.value = false;
    const message = e instanceof Error ? e.message : 'Map mount failed';
    mountError.value = message;
    emit('error', message);
  }
});

onBeforeUnmount(() => {
  mapMountedOk.value = false;
  props.mapProvider.destroy();
});
</script>

<template>
  <div class="gpx-map relative min-h-0 min-w-0 size-full">
    <div
      v-if="mountError"
      class="flex min-h-[8rem] size-full items-center justify-center px-4 py-8 text-center text-sm text-red-700"
    >
      {{ mountError }}
    </div>
    <template v-else>
      <div ref="root" class="absolute inset-0 z-0 min-h-0 min-w-0" />
      <div class="pointer-events-none absolute inset-0 z-[5] min-h-0 min-w-0">
        <div
          class="pointer-events-auto absolute left-3 top-3 flex max-w-[min(100%-1.5rem,20rem)] flex-col gap-2"
        >
          <slot name="top-left" />
        </div>
        <div
          class="pointer-events-auto absolute left-1/2 top-3 flex max-w-[min(calc(100%-1.5rem),36rem)] -translate-x-1/2 flex-row flex-wrap justify-center gap-2 px-1"
        >
          <slot name="top-center" />
        </div>
        <div
          class="pointer-events-auto absolute right-3 top-3 flex max-w-[min(100%-1.5rem,20rem)] flex-col items-end gap-2"
        >
          <slot name="top-right" />
        </div>
        <div
          class="pointer-events-auto absolute bottom-3 left-3 flex max-w-[min(100%-1.5rem,24rem)] flex-col gap-2"
        >
          <slot name="bottom-left" />
        </div>
        <div
          class="pointer-events-auto absolute bottom-3 left-1/2 flex max-w-[min(calc(100%-1.5rem),36rem)] -translate-x-1/2 flex-row flex-wrap justify-center gap-2 px-1"
        >
          <slot name="bottom-center" />
        </div>
        <div
          class="pointer-events-auto absolute bottom-3 right-3 flex flex-col items-end gap-2"
        >
          <slot name="bottom-right" />
        </div>
        <div
          class="pointer-events-auto absolute left-3 top-1/2 flex max-w-[min(100%-1.5rem,24rem)] -translate-y-1/2 flex-col gap-2"
        >
          <slot name="left-center" />
        </div>
        <div
          class="pointer-events-auto absolute right-3 top-1/2 flex max-w-[min(100%-1.5rem,20rem)] -translate-y-1/2 flex-col items-end gap-2"
        >
          <slot name="right-center" />
        </div>
      </div>
    </template>
  </div>
</template>
